import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ContaContabilResponse, ItemCifResponse } from '@triade/contracts';
import { Search } from 'lucide-react';
import { useContasContabeis, useItensCif } from '@/api/itens-cif';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebounced } from '@/hooks/use-debounced';
import { BotaoRelatorios } from '@/relatorios';
import { useSessao } from '@/lib/session';
import { cn } from '@/lib/utils';
import { ContaContabilDialog } from './conta-contabil-dialog';
import { ItemCifDialog } from './item-cif-dialog';
import { resumoApropriacao } from './item-form';

export const ABAS_ITENS_CIF = ['itens', 'contas'] as const;
export type AbaItensCif = (typeof ABAS_ITENS_CIF)[number];

/** Busca da URL: `aba` (padrao = itens). Valor invalido e descartado. */
export function lerBuscaItensCif(bruto: Record<string, unknown>): { aba?: AbaItensCif } {
  const aba = ABAS_ITENS_CIF.find((a) => a === bruto.aba);
  return aba ? { aba } : {};
}

/** Tela "Itens de CIF" (/custos/itens-cif): itens recorrentes e contas contabeis. Edicao em dialogo (rateio.editar). */
export function ItensCifPage({ aba = 'itens' }: { aba?: AbaItensCif }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('rateio.editar');

  const mudarAba = (v: string) => void navigate({ to: '/custos/itens-cif', search: { aba: v as AbaItensCif }, replace: true });

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div>
        <div className="text-small text-ink-muted">
          Custos <span aria-hidden="true">/</span> Itens de CIF
        </div>
        <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Itens de CIF</h1>
        <p className="text-small text-ink-secondary">Lançamentos recorrentes de CIF e as contas contábeis do plano</p>
      </div>

      {empresaId === null ? (
        <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">Escolha a empresa no menu lateral para ver os itens de CIF.</p>
      ) : (
        <Tabs value={aba} onValueChange={mudarAba} className="gap-4">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="itens">Itens</TabsTrigger>
            <TabsTrigger value="contas">Contas contábeis</TabsTrigger>
          </TabsList>
          <TabsContent value="itens">
            <ListaItens podeEditar={podeEditar} />
          </TabsContent>
          <TabsContent value="contas">
            <ListaContas podeEditar={podeEditar} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function ListaItens({ podeEditar }: { podeEditar: boolean }) {
  const [termo, setTermo] = useState('');
  const termoAtrasado = useDebounced(termo.trim(), 300);
  const lista = useItensCif({ ...(termoAtrasado ? { q: termoAtrasado } : {}), limit: 200 });
  const [editando, setEditando] = useState<ItemCifResponse | 'novo' | null>(null);
  const itens = lista.data ?? [];
  const pagItens = usePaginarLista(itens);

  let corpo;
  if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar os itens de CIF" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando itens de CIF…</Mensagem>;
  else if (itens.length === 0) corpo = <Mensagem papel="status">{termoAtrasado ? 'Nenhum item com essa busca.' : 'Nenhum item de CIF cadastrado.'}</Mensagem>;
  else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-small" aria-label="Lista de itens de CIF">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Nome</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Contas</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Apropriação padrão</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {pagItens.visiveis.map((i) => (
              <tr
                key={i.id}
                data-testid="linha-item-cif"
                onClick={() => setEditando(i)}
                className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted')}
              >
                <td className="px-4 py-3 font-semibold">{i.nome}</td>
                <td className="px-3 py-3 text-ink-secondary">{i.contas.length > 0 ? i.contas.map((c) => c.codigo).join(', ') : '—'}</td>
                <td className="px-3 py-3 text-ink-secondary">{resumoApropriacao(i.apropriacao)}</td>
                <td className="px-4 py-3">{i.ativo ? <span className="rounded-full bg-success-bg px-2 py-0.5 text-caption font-semibold text-success-ink">Ativo</span> : <span className="text-ink-muted">Inativo</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section aria-label="Itens de CIF" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
        <div className="flex h-[34px] w-[260px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
          <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
          <label htmlFor="item-cif-q" className="sr-only">Buscar item de CIF</label>
          <input id="item-cif-q" type="search" value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Nome do item" autoComplete="off" className="w-full min-w-0 bg-transparent text-small outline-none" />
        </div>
      </div>
      {corpo}
      {editando !== null && (
        <ItemCifDialog key={editando === 'novo' ? 'novo' : editando.id} item={editando === 'novo' ? null : editando} podeEditar={podeEditar} onFechar={() => setEditando(null)} />
      )}
      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Novo item', onClick: () => setEditando('novo') } : undefined}
        paginacao={lista.data ? pagItens.paginacao : undefined}
        relatorios={<BotaoRelatorios tela="itens-cif" />}
      />
    </section>
  );
}

function ListaContas({ podeEditar }: { podeEditar: boolean }) {
  const [termo, setTermo] = useState('');
  const termoAtrasado = useDebounced(termo.trim(), 300);
  const lista = useContasContabeis({ ...(termoAtrasado ? { q: termoAtrasado } : {}), limit: 200 });
  const [editando, setEditando] = useState<ContaContabilResponse | 'novo' | null>(null);
  const contas = lista.data ?? [];
  const pagContas = usePaginarLista(contas);

  let corpo;
  if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar as contas contábeis" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando contas contábeis…</Mensagem>;
  else if (contas.length === 0) corpo = <Mensagem papel="status">{termoAtrasado ? 'Nenhuma conta com essa busca.' : 'Nenhuma conta contábil cadastrada.'}</Mensagem>;
  else {
    corpo = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse text-small" aria-label="Lista de contas contábeis">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="px-4 py-2.5 font-medium">Código</th>
              <th scope="col" className="px-3 py-2.5 font-medium">Descrição</th>
              <th scope="col" className="px-4 py-2.5 font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {pagContas.visiveis.map((c) => (
              <tr key={c.id} data-testid="linha-conta-contabil" onClick={() => setEditando(c)} className="cursor-pointer border-t border-line-soft hover:bg-surface-muted">
                <td className="px-4 py-3 font-mono font-semibold">{c.codigo}</td>
                <td className="px-3 py-3 text-ink-secondary">{c.descricao}</td>
                <td className="px-4 py-3">{c.ativo ? <span className="rounded-full bg-success-bg px-2 py-0.5 text-caption font-semibold text-success-ink">Ativa</span> : <span className="text-ink-muted">Inativa</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section aria-label="Contas contábeis" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3.5">
        <div className="flex h-[34px] w-[260px] max-w-full items-center gap-2 rounded-[9px] border border-input bg-card px-2.5 focus-within:border-primary focus-within:shadow-focus">
          <Search aria-hidden="true" className="size-[15px] text-ink-muted" />
          <label htmlFor="conta-q" className="sr-only">Buscar conta contábil</label>
          <input id="conta-q" type="search" value={termo} onChange={(e) => setTermo(e.target.value)} placeholder="Código ou descrição" autoComplete="off" className="w-full min-w-0 bg-transparent text-small outline-none" />
        </div>
      </div>
      {corpo}
      {editando !== null && (
        <ContaContabilDialog key={editando === 'novo' ? 'novo' : editando.id} conta={editando === 'novo' ? null : editando} podeEditar={podeEditar} onFechar={() => setEditando(null)} />
      )}
      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Nova conta', onClick: () => setEditando('novo') } : undefined}
        paginacao={lista.data ? pagContas.paginacao : undefined}
        relatorios={<BotaoRelatorios tela="itens-cif" />}
      />
    </section>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
